<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
            <div class="md:flex md:items-center md:justify-between">
                <div class="flex-1 min-w-0">
                    <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('Mirror users') }}</h1>
                    <p class="text-sm text-gray-500 mt-1">
                        {{ $t('Users synced from GCC SSO. Role follows the mapping, position can be toggled for ambiguous roles. Set a local password as an emergency login for when SSO is disabled.') }}
                    </p>
                </div>
            </div>
        </div>
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="my-6 bg-white shadow overflow-hidden sm:rounded-md">
                <loading :status="loading"/>
                <template v-if="items.length > 0">
                    <div class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                            <tr>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Name') }}</th>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('GCC role') }}</th>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Role') }}</th>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Departments') }}</th>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Position') }}</th>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Local password') }}</th>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Actions') }}</th>
                            </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-200">
                            <tr v-for="item in items" :key="item.id">
                                <td class="px-4 py-3">
                                    <div class="text-sm font-medium text-gray-900">{{ item.name }}</div>
                                    <div class="text-sm text-gray-500">{{ item.email }}</div>
                                </td>
                                <td class="px-4 py-3 text-sm text-gray-700">{{ item.gcc_role_name || '-' }}</td>
                                <td class="px-4 py-3 text-sm text-gray-700">{{ item.role ? item.role.name : '-' }}</td>
                                <td class="px-4 py-3 text-sm text-gray-700">
                                    <template v-if="item.departments.length > 0">
                                        {{ item.departments.map(function (department) { return department.name; }).join(', ') }}
                                    </template>
                                    <template v-else>-</template>
                                </td>
                                <td class="px-4 py-3">
                                    <template v-if="item.can_toggle_position">
                                        <button
                                            class="btn btn-blue shadow-sm rounded-md"
                                            :disabled="togglingId === item.id"
                                            @click="togglePosition(item)"
                                        >
                                            {{ (item.mirror_position === 'manager' ? $t('Manager') : $t('Staff')) }}
                                            ⇄
                                        </button>
                                    </template>
                                    <template v-else>
                                        <span class="text-sm text-gray-500">{{ $t('Fixed') }}</span>
                                    </template>
                                </td>
                                <td class="px-4 py-3">
                                    <button
                                        class="btn btn-secondary shadow-sm rounded-md"
                                        type="button"
                                        @click="openPasswordModal(item)"
                                    >
                                        {{ $t('Set password') }}
                                    </button>
                                </td>
                                <td class="px-4 py-3">
                                    <button
                                        class="btn btn-red shadow-sm rounded-md"
                                        :disabled="deletingId === item.id"
                                        type="button"
                                        @click="openDeleteModal(item)"
                                    >
                                        {{ $t('Delete') }}
                                    </button>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-if="pagination.totalPages > 1" class="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                        <button
                            class="btn btn-secondary rounded-md"
                            :disabled="pagination.currentPage <= 1"
                            @click="changePage(pagination.currentPage - 1)"
                        >
                            {{ $t('Previous') }}
                        </button>
                        <span class="text-sm text-gray-500">{{ pagination.currentPage }} / {{ pagination.totalPages }}</span>
                        <button
                            class="btn btn-secondary rounded-md"
                            :disabled="pagination.currentPage >= pagination.totalPages"
                            @click="changePage(pagination.currentPage + 1)"
                        >
                            {{ $t('Next') }}
                        </button>
                    </div>
                </template>
                <template v-else-if="!loading">
                    <div class="h-full flex">
                        <div class="m-auto">
                            <div class="grid grid-cols-1 justify-items-center h-full w-full px-4 py-10">
                                <div class="flex justify-center items-center">
                                    <svg-vue class="h-full h-auto w-64 mb-12" icon="undraw.browsing"></svg-vue>
                                </div>
                                <div class="flex justify-center items-center">
                                    <div class="w-full font-semibold text-2xl">{{ $t('No records found') }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </div>
        <div v-show="passwordModal" class="fixed z-20 inset-0 overflow-y-auto">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div class="fixed inset-0 transition-opacity">
                    <div class="absolute inset-0 bg-gray-500 opacity-75" @click="closePasswordModal"></div>
                </div>
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen"></span>&#8203;
                <div
                    aria-labelledby="password-modal-headline"
                    aria-modal="true"
                    class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
                    role="dialog"
                >
                    <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <h3 id="password-modal-headline" class="text-lg leading-6 font-medium text-gray-900">
                            {{ $t('Set local password') }}
                        </h3>
                        <p class="mt-1 text-sm text-gray-500">
                            {{ $t('Emergency login for when GCC SSO is disabled. This password is not used while SSO is active.') }}
                        </p>
                        <div class="mt-4">
                            <label class="block text-sm font-medium leading-5 text-gray-700" for="mirror-local-password">{{ $t('Password') }}</label>
                            <input
                                id="mirror-local-password"
                                v-model="localPassword.password"
                                autocomplete="new-password"
                                class="form-input block w-full mt-1 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                type="password"
                            >
                        </div>
                        <div class="mt-4">
                            <label class="block text-sm font-medium leading-5 text-gray-700" for="mirror-local-password-confirmation">{{ $t('Confirm password') }}</label>
                            <input
                                id="mirror-local-password-confirmation"
                                v-model="localPassword.password_confirmation"
                                autocomplete="new-password"
                                class="form-input block w-full mt-1 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                type="password"
                            >
                        </div>
                    </div>
                    <div class="bg-gray-100 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            class="btn btn-blue shadow-sm rounded-md sm:ml-2"
                            :disabled="savingPassword"
                            type="button"
                            @click="saveLocalPassword"
                        >
                            {{ $t('Save') }}
                        </button>
                        <button
                            class="btn btn-secondary shadow-sm rounded-md"
                            type="button"
                            @click="closePasswordModal"
                        >
                            {{ $t('Cancel') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div v-show="deleteModal" class="fixed z-20 inset-0 overflow-y-auto">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div class="fixed inset-0 transition-opacity">
                    <div class="absolute inset-0 bg-gray-500 opacity-75" @click="closeDeleteModal"></div>
                </div>
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen"></span>&#8203;
                <div
                    aria-labelledby="mirror-delete-modal-headline"
                    aria-modal="true"
                    class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
                    role="dialog"
                >
                    <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <h3 id="mirror-delete-modal-headline" class="text-lg leading-6 font-medium text-gray-900">
                            {{ $t('Delete account') }}
                        </h3>
                        <p class="mt-2 text-sm text-gray-500">
                            {{ $t('Are you sure you want to delete the user?') }}
                            {{ $t('The account will be created again automatically on the next GCC SSO login, but the local password and position override will be lost') }}.
                        </p>
                    </div>
                    <div class="bg-gray-100 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            class="btn btn-red shadow-sm rounded-md sm:ml-2"
                            :disabled="deleting"
                            type="button"
                            @click="destroyUser"
                        >
                            {{ $t('Delete') }}
                        </button>
                        <button
                            class="btn btn-secondary shadow-sm rounded-md"
                            type="button"
                            @click="closeDeleteModal"
                        >
                            {{ $t('Cancel') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
export default {
    name: "gcc-mirror-list",
    metaInfo() {
        return {
            title: this.$i18n.t('Mirror users')
        }
    },
    data() {
        return {
            loading: true,
            togglingId: null,
            passwordModal: false,
            savingPassword: false,
            passwordUser: null,
            deleteModal: false,
            deleting: false,
            deleteTarget: null,
            localPassword: {
                password: '',
                password_confirmation: '',
            },
            items: [],
            pagination: {
                currentPage: 1,
                perPage: 10,
                total: 0,
                totalPages: 1
            },
        }
    },
    mounted() {
        this.getUsers();
    },
    methods: {
        getUsers() {
            const self = this;
            self.loading = true;
            axios.get('api/dashboard/admin/gcc-mirror', {params: {page: self.pagination.currentPage, perPage: self.pagination.perPage}}).then(function (response) {
                self.items = response.data.items;
                self.pagination = response.data.pagination;
                self.loading = false;
            }).catch(function () {
                self.loading = false;
            });
        },
        changePage(page) {
            this.pagination.currentPage = page;
            this.getUsers();
        },
        togglePosition(item) {
            const self = this;
            self.togglingId = item.id;
            axios.post('api/dashboard/admin/gcc-mirror/' + item.id + '/position').then(function (response) {
                self.togglingId = null;
                if (response.data.user) {
                    const index = self.items.findIndex(function (entry) {
                        return entry.id === response.data.user.id;
                    });
                    if (index !== -1) {
                        self.items.splice(index, 1, response.data.user);
                    }
                }
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: response.data.message,
                    type: 'success'
                });
            }).catch(function () {
                self.togglingId = null;
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('An error occurred while saving data').toString(),
                    type: 'error'
                });
            });
        },
        openPasswordModal(item) {
            this.passwordUser = item;
            this.localPassword.password = '';
            this.localPassword.password_confirmation = '';
            this.passwordModal = true;
        },
        closePasswordModal() {
            this.passwordModal = false;
            this.passwordUser = null;
        },
        saveLocalPassword() {
            const self = this;
            if (!self.passwordUser) {
                return;
            }
            if (!self.localPassword.password || self.localPassword.password.length < 6) {
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('The password must be at least 6 characters').toString(),
                    type: 'error'
                });
                return;
            }
            if (self.localPassword.password !== self.localPassword.password_confirmation) {
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('The password confirmation does not match').toString(),
                    type: 'error'
                });
                return;
            }
            self.savingPassword = true;
            axios.post('api/dashboard/admin/users/' + self.passwordUser.id + '/reset-password', self.localPassword).then(function (response) {
                self.savingPassword = false;
                self.closePasswordModal();
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: (response.data && response.data.message) ? response.data.message : self.$i18n.t('Password reset successfully').toString(),
                    type: 'success'
                });
            }).catch(function (error) {
                self.savingPassword = false;
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: (error.response && error.response.data && error.response.data.message) ? error.response.data.message : self.$i18n.t('An error occurred while saving data').toString(),
                    type: 'error'
                });
            });
        },
        openDeleteModal(item) {
            this.deleteTarget = item;
            this.deleteModal = true;
        },
        closeDeleteModal() {
            this.deleteModal = false;
            this.deleteTarget = null;
        },
        destroyUser() {
            const self = this;
            if (!self.deleteTarget) {
                return;
            }
            self.deleting = true;
            axios.delete('api/dashboard/admin/gcc-mirror/' + self.deleteTarget.id).then(function (response) {
                self.deleting = false;
                self.items = self.items.filter(function (entry) {
                    return entry.id !== self.deleteTarget.id;
                });
                self.pagination.total -= 1;
                self.closeDeleteModal();
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: (response.data && response.data.message) ? response.data.message : self.$i18n.t('Data deleted successfully').toString(),
                    type: 'success'
                });
            }).catch(function (error) {
                self.deleting = false;
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: (error.response && error.response.data && error.response.data.message) ? error.response.data.message : self.$i18n.t('An error occurred while deleting data').toString(),
                    type: 'error'
                });
            });
        }
    }
}
</script>
